import emailjs from "@emailjs/browser";
import {
  collection,
  query,
  where,
  getDocs,
  addDoc,
  deleteDoc,
  doc,
  serverTimestamp,
  Timestamp,
  FieldValue,
} from "firebase/firestore";
import { db } from "@/firebase/config";

type EmailParams = Record<string, string>;

interface EmailError {
  message: string;
  name: string;
  code?: string;
}

interface NewsletterSubscriber {
  email: string;
  createdAt: Timestamp | FieldValue;
  source: string;
  subscriptionPage: string;
  userAgent: string;
}

export const getAllUniqueSubscribers = async (): Promise<string[]> => {
  try {
    // Get verified users
    const usersRef = collection(db, "users");
    const verifiedUsersQuery = query(
      usersRef,
      where("email_verified", "==", true)
    );

    // Get newsletter subscribers
    const newsletterRef = collection(db, "NewsLetterWeb");

    // Run both queries in parallel
    const [verifiedUsersSnapshot, newsletterSnapshot] = await Promise.all([
      getDocs(verifiedUsersQuery),
      getDocs(newsletterRef),
    ]);

    // Collect all emails in a Set for automatic deduplication
    const uniqueEmails = new Set<string>();

    // Add verified users' emails
    verifiedUsersSnapshot.docs.forEach((doc) => {
      const email = doc.data().email?.toLowerCase();
      if (email) uniqueEmails.add(email);
    });

    // Add newsletter subscribers' emails
    newsletterSnapshot.docs.forEach((doc) => {
      const email = doc.data().email?.toLowerCase();
      if (email) uniqueEmails.add(email);
    });

    return Array.from(uniqueEmails);
  } catch (error) {
    console.error("Error fetching unique subscribers:", error);
    throw error;
  }
};

export const getVerifiedEmails = async () => {
  try {
    console.log("🔍 Fetching verified emails...");
    const usersRef = collection(db, "users");
    const q = query(usersRef, where("email_verified", "==", true));
    const querySnapshot = await getDocs(q);

    console.log("📊 Verified users query result:", {
      totalDocs: querySnapshot.docs.length,
      sampleData: querySnapshot.docs.slice(0, 3).map((doc) => ({
        id: doc.id,
        data: doc.data(),
      })),
    });

    const emails = querySnapshot.docs
      .map((doc) => {
        const data = doc.data();
        return data.email?.toLowerCase();
      })
      .filter((email) => email); // Filter out undefined emails

    console.log("✅ Verified emails extracted:", {
      count: emails.length,
      sample: emails.slice(0, 3),
    });

    return emails;
  } catch (error) {
    console.error("Error fetching verified emails:", error);
    throw error;
  }
};

export const addNewsletterSubscriber = async (
  email: string,
  source: string = "website_footer"
) => {
  try {
    // Check if email already exists
    const subscribersRef = collection(db, "NewsLetterWeb");
    const q = query(subscribersRef, where("email", "==", email.toLowerCase()));
    const querySnapshot = await getDocs(q);

    if (!querySnapshot.empty) {
      throw new Error("Email already subscribed");
    }

    // Add new subscriber with proper schema
    const newSubscriber: NewsletterSubscriber = {
      email: email.toLowerCase(),
      createdAt: serverTimestamp(),
      source: source,
      subscriptionPage: window.location.pathname,
      userAgent: navigator.userAgent,
    };

    await addDoc(subscribersRef, newSubscriber);
    return true;
  } catch (error) {
    console.error("Error adding newsletter subscriber:", error);
    throw error;
  }
};

export const getNewsletterSubscribers = async () => {
  try {
    console.log("🔍 Fetching newsletter subscribers...");
    const subscribersRef = collection(db, "NewsLetterWeb");
    const querySnapshot = await getDocs(subscribersRef);

    console.log("📊 Newsletter subscribers query result:", {
      totalDocs: querySnapshot.docs.length,
      sampleData: querySnapshot.docs.slice(0, 3).map((doc) => ({
        id: doc.id,
        data: doc.data(),
      })),
    });

    const subscribers = querySnapshot.docs.map((doc) => {
      const data = doc.data() as NewsletterSubscriber;
      return {
        id: doc.id,
        ...data,
      };
    });

    console.log("✅ Newsletter subscribers extracted:", {
      count: subscribers.length,
      sample: subscribers
        .slice(0, 3)
        .map((sub) => ({ id: sub.id, email: sub.email })),
    });

    return subscribers;
  } catch (error) {
    console.error("Error fetching newsletter subscribers:", error);
    throw error;
  }
};

export const getEmailsFromSelectedLists = async (
  selectedLists: string[]
): Promise<string[]> => {
  try {
    const uniqueEmails = new Set<string>();

    // If "verified-users" is selected, get verified user emails
    if (selectedLists.includes("verified-users")) {
      const usersRef = collection(db, "users");
      const verifiedUsersQuery = query(
        usersRef,
        where("email_verified", "==", true)
      );
      const verifiedUsersSnapshot = await getDocs(verifiedUsersQuery);

      verifiedUsersSnapshot.docs.forEach((doc) => {
        const email = doc.data().email?.toLowerCase();
        if (email) uniqueEmails.add(email);
      });
    }

    // If "newsletter-subscribers" is selected, get newsletter subscriber emails
    if (selectedLists.includes("newsletter-subscribers")) {
      const newsletterRef = collection(db, "NewsLetterWeb");
      const newsletterSnapshot = await getDocs(newsletterRef);

      newsletterSnapshot.docs.forEach((doc) => {
        const email = doc.data().email?.toLowerCase();
        if (email) uniqueEmails.add(email);
      });
    }

    return Array.from(uniqueEmails);
  } catch (error) {
    console.error("Error fetching emails from selected lists:", error);
    throw error;
  }
};

export const sendBulkEmail = async (
  templateId: string,
  serviceId: string,
  publicKey: string,
  subject: string,
  htmlContent: string,
  emails: string[],
  title: string = "Newsletter"
) => {
  // Initialize EmailJS with the public key
  emailjs.init(publicKey);

  // Ensure all emails are lowercase for consistency
  const normalizedEmails = [
    ...new Set(emails.map((email) => email.toLowerCase())),
  ];

  console.log("📧 Email Service Configuration:", {
    templateId,
    serviceId,
    publicKey: publicKey ? "✓ Present" : "✗ Missing",
    emailCount: normalizedEmails.length,
  });

  const results = {
    successful: [] as string[],
    failed: [] as { email: string; error: EmailError }[],
  };

  for (const email of normalizedEmails) {
    try {
      // Generate unsubscribe URL with email encoded
      const unsubscribeUrl = `${
        window.location.origin
      }/unsubscribe?email=${encodeURIComponent(email)}`;

      const templateParams: EmailParams = {
        subject: subject,
        html_content: htmlContent,
        unsubscribe_url: unsubscribeUrl,
        title: title,
        to_email: email,
        current_year: new Date().getFullYear().toString(),
      };

      console.log("📤 Sending email with params:", {
        to_email: email,
        subject: subject,
        template_id: templateId,
        service_id: serviceId,
        content_length: htmlContent.length,
        unsubscribe_url: unsubscribeUrl,
      });

      const response = await emailjs.send(
        serviceId,
        templateId,
        templateParams,
        publicKey
      );

      console.log("📬 EmailJS Response:", response);
      results.successful.push(email);

      // Add a small delay between emails to avoid rate limiting
      if (normalizedEmails.length > 1) {
        await new Promise((resolve) => setTimeout(resolve, 1000));
      }
    } catch (error) {
      console.error("❌ EmailJS Error:", {
        email,
        templateId,
        serviceId,
        error:
          error instanceof Error
            ? {
                name: error.name,
                message: error.message,
                stack: error.stack,
              }
            : error,
      });
      results.failed.push({
        email,
        error: {
          message: error instanceof Error ? error.message : "Unknown error",
          name: error instanceof Error ? error.name : "Error",
        },
      });
    }
  }

  console.log("📊 Final Email Results:", {
    successful: results.successful.length,
    failed: results.failed.length,
    details: results,
  });

  return results;
};

// Send welcome email after newsletter submission
export const sendNewsletterWelcomeEmail = async (
  subscriberEmail: string,
  serviceId: string,
  templateId: string,
  publicKey: string,
  newsletterTitle: string = "Newsletter"
) => {
  try {
    emailjs.init(publicKey);

    // Generate unsubscribe URL
    const unsubscribeUrl = `${
      window.location.origin
    }/unsubscribe?email=${encodeURIComponent(subscriberEmail)}`;

    // Create welcome email content
    const welcomeHtmlContent = `
      <div style="max-width: 600px; margin: 0 auto; font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif; background-color: #ffffff;">
        <!-- Header -->
        <div style="background: linear-gradient(135deg, #2D5016 0%, #4A7C59 100%); padding: 40px 20px; text-align: center;">
          <img src="https://cittasya.com/logo-new.svg" alt="Cittasya" style="height: 50px; margin-bottom: 20px;">
          <h1 style="color: #ffffff; margin: 0; font-size: 28px; font-weight: 600;">
            Welcome to Cittasya! 🕉️
          </h1>
          <p style="color: #ffffff; margin: 10px 0 0 0; font-size: 16px; opacity: 0.9;">
            Thank you for joining our mindful community
          </p>
        </div>

        <!-- Main Content -->
        <div style="padding: 40px 30px;">
          <h2 style="color: #2D5016; margin: 0 0 20px 0; font-size: 24px; font-weight: 600;">
            🙏 Namaste & Welcome!
          </h2>
          
          <p style="color: #4A5568; line-height: 1.6; margin: 0 0 20px 0; font-size: 16px;">
            We're delighted to have you as part of the Cittasya family! Your journey towards mindfulness, 
            wellness, and inner peace begins here.
          </p>

          <div style="background-color: #F7FAFC; border-left: 4px solid #D69E2E; padding: 20px; margin: 30px 0; border-radius: 8px;">
            <h3 style="color: #2D5016; margin: 0 0 15px 0; font-size: 18px; font-weight: 600;">
              📧 Newsletter Received: ${newsletterTitle}
            </h3>
            <p style="color: #4A5568; margin: 0; line-height: 1.6;">
              Thank you for engaging with our latest newsletter. We hope you found it insightful and valuable 
              for your wellness journey.
            </p>
          </div>

          <h3 style="color: #2D5016; margin: 30px 0 15px 0; font-size: 20px; font-weight: 600;">
            🌱 What's Next?
          </h3>
          
          <ul style="color: #4A5568; line-height: 1.8; padding-left: 20px; margin: 0 0 30px 0;">
            <li><strong>Download Our App:</strong> Access guided meditations, yoga sessions, and mindfulness practices on-the-go</li>
            <li><strong>Explore Our Content:</strong> Discover articles, videos, and resources on yoga, meditation, and Ayurveda</li>
            <li><strong>Join Our Community:</strong> Connect with like-minded individuals on your wellness journey</li>
            <li><strong>Stay Updated:</strong> Receive regular insights, tips, and inspiration in your inbox</li>
          </ul>

          <!-- App Download Buttons -->
          <div style="text-align: center; margin: 30px 0; padding: 25px; background-color: #F7FAFC; border-radius: 12px;">
            <h4 style="color: #2D5016; margin: 0 0 20px 0; font-size: 18px; font-weight: 600;">
              📱 Download Cittasya App
            </h4>
            <p style="color: #4A5568; margin: 0 0 20px 0; font-size: 14px;">
              Take your wellness journey anywhere with our mobile app
            </p>
            <div style="display: inline-block; margin: 0 10px;">
              <a href="https://apps.apple.com/app/cittasya" style="text-decoration: none;">
                <img src="https://cittasya.com/apple.svg" alt="Download on App Store" style="height: 40px;">
              </a>
            </div>
            <div style="display: inline-block; margin: 0 10px;">
              <a href="https://play.google.com/store/apps/details?id=com.cittasya" style="text-decoration: none;">
                <img src="https://cittasya.com/google-play.svg" alt="Get it on Google Play" style="height: 40px;">
              </a>
            </div>
          </div>

          <!-- Sanskrit Quote -->
          <div style="text-align: center; margin: 30px 0; padding: 25px; background: linear-gradient(135deg, #FFF5F5 0%, #FED7E2 100%); border-radius: 12px; border: 1px solid #FBB6CE;">
            <p style="color: #744210; font-style: italic; margin: 0 0 10px 0; font-size: 16px; font-weight: 500;">
              "योगः चित्तवृत्ति निरोधः"
            </p>
            <p style="color: #9C4221; margin: 0; font-size: 14px;">
              "Yoga is the cessation of fluctuations of the mind" - Patanjali Yoga Sutras
            </p>
          </div>
        </div>

        <!-- Footer -->
        <div style="background-color: #2D5016; color: #ffffff; padding: 30px 20px; text-align: center;">
          <div style="margin-bottom: 20px;">
            <a href="https://www.instagram.com/cittasya.official" style="color: #ffffff; text-decoration: none; margin: 0 10px; font-size: 24px;">📷</a>
            <a href="https://www.linkedin.com/company/cittasya" style="color: #ffffff; text-decoration: none; margin: 0 10px; font-size: 24px;">💼</a>
            <a href="https://www.youtube.com/@cittasya" style="color: #ffffff; text-decoration: none; margin: 0 10px; font-size: 24px;">🎥</a>
            <a href="https://twitter.com/cittasya" style="color: #ffffff; text-decoration: none; margin: 0 10px; font-size: 24px;">🐦</a>
          </div>
          
          <p style="margin: 0 0 10px 0; font-size: 14px; opacity: 0.9;">
            🌿 Cultivating mindfulness, one breath at a time
          </p>
          
          <p style="margin: 0 0 20px 0; font-size: 12px; opacity: 0.7;">
            © ${new Date().getFullYear()} Cittasya Wellness. All rights reserved.
          </p>
          
          <div style="border-top: 1px solid rgba(255,255,255,0.2); padding-top: 20px; margin-top: 20px;">
            <p style="margin: 0; font-size: 12px; opacity: 0.8;">
              You're receiving this welcome email because you've engaged with our newsletter.
            </p>
            <p style="margin: 5px 0 0 0; font-size: 12px;">
              <a href="${unsubscribeUrl}" style="color: #D69E2E; text-decoration: none;">
                Unsubscribe from future emails
              </a> | 
              <a href="https://cittasya.com/privacy" style="color: #D69E2E; text-decoration: none;">
                Privacy Policy
              </a>
            </p>
          </div>
        </div>
      </div>
    `;

    const templateParams: EmailParams = {
      subject: "Welcome to Cittasya! 🕉️ Your Wellness Journey Begins",
      html_content: welcomeHtmlContent,
      unsubscribe_url: unsubscribeUrl,
      title: "Welcome to Cittasya",
      to_email: subscriberEmail,
      current_year: new Date().getFullYear().toString(),
      newsletter_title: newsletterTitle,
    };

    console.log("📤 Sending welcome email after newsletter:", {
      to_email: subscriberEmail,
      newsletter_title: newsletterTitle,
      template_id: templateId,
      service_id: serviceId,
    });

    const response = await emailjs.send(
      serviceId,
      templateId,
      templateParams,
      publicKey
    );

    console.log("✅ Newsletter welcome email sent successfully:", response);
    return response;
  } catch (error) {
    console.error("❌ Error sending newsletter welcome email:", error);
    throw error;
  }
};

export const unsubscribeEmail = async (email: string): Promise<boolean> => {
  try {
    const normalizedEmail = email.toLowerCase();

    // Remove from NewsLetterWeb collection
    const subscribersRef = collection(db, "NewsLetterWeb");
    const q = query(subscribersRef, where("email", "==", normalizedEmail));
    const querySnapshot = await getDocs(q);

    if (querySnapshot.empty) {
      console.log(
        "Email not found in newsletter subscribers:",
        normalizedEmail
      );
      return false;
    }

    // Delete all matching documents (in case of duplicates)
    const deletePromises = querySnapshot.docs.map((docSnapshot) =>
      deleteDoc(doc(db, "NewsLetterWeb", docSnapshot.id))
    );

    await Promise.all(deletePromises);

    console.log("✅ Successfully unsubscribed:", normalizedEmail);
    return true;
  } catch (error) {
    console.error("❌ Error unsubscribing email:", error);
    throw error;
  }
};

export const testFirebaseConnection = async () => {
  try {
    console.log("🧪 Testing Firebase connection...");

    // Test users collection
    const usersRef = collection(db, "users");
    const usersSnapshot = await getDocs(usersRef);
    console.log("👥 Users collection:", {
      totalUsers: usersSnapshot.docs.length,
      sampleUser: usersSnapshot.docs[0]?.data(),
    });

    // Test newsletter collection
    const newsletterRef = collection(db, "NewsLetterWeb");
    const newsletterSnapshot = await getDocs(newsletterRef);
    console.log("📧 Newsletter collection:", {
      totalSubscribers: newsletterSnapshot.docs.length,
      sampleSubscriber: newsletterSnapshot.docs[0]?.data(),
    });

    // Test verified users specifically
    const verifiedQuery = query(usersRef, where("email_verified", "==", true));
    const verifiedSnapshot = await getDocs(verifiedQuery);
    console.log("✅ Verified users:", {
      totalVerified: verifiedSnapshot.docs.length,
      sampleVerified: verifiedSnapshot.docs[0]?.data(),
    });

    return {
      totalUsers: usersSnapshot.docs.length,
      totalNewsletterSubscribers: newsletterSnapshot.docs.length,
      totalVerifiedUsers: verifiedSnapshot.docs.length,
    };
  } catch (error) {
    console.error("❌ Firebase connection test failed:", error);
    throw error;
  }
};

export const initEmailJS = (publicKey: string) => {
  emailjs.init(publicKey);
};

// Send welcome email to new subscriber
export const sendWelcomeEmail = async (
  subscriberEmail: string,
  serviceId: string,
  templateId: string,
  publicKey: string
) => {
  try {
    emailjs.init(publicKey);

    const unsubscribeUrl = `${
      window.location.origin
    }/unsubscribe?email=${encodeURIComponent(subscriberEmail)}`;

    const templateParams = {
      to_email: subscriberEmail,
      subject: "Welcome to Cittasya Newsletter! 🕉️",
      user_name: subscriberEmail.split("@")[0], // Use email prefix as name
      unsubscribe_url: unsubscribeUrl,
      website_url: window.location.origin,
    };

    const response = await emailjs.send(
      serviceId,
      templateId,
      templateParams,
      publicKey
    );
    console.log("✅ Welcome email sent successfully:", response);
    return response;
  } catch (error) {
    console.error("❌ Error sending welcome email:", error);
    throw error;
  }
};

// Send notification email to admin about new subscription
export const sendAdminNotificationEmail = async (
  subscriberEmail: string,
  serviceId: string,
  templateId: string,
  publicKey: string,
  subscriptionSource: string = "website"
) => {
  try {
    emailjs.init(publicKey);

    const currentDate = new Date();
    const formattedDate = currentDate.toLocaleString("en-US", {
      year: "numeric",
      month: "long",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
      timeZoneName: "short",
    });

    // Generate unsubscribe URL (even for admin emails, for transparency)
    const unsubscribeUrl = `${
      window.location.origin
    }/unsubscribe?email=${encodeURIComponent(subscriberEmail)}`;

    // Create HTML content with subscriber details
    const htmlContent = `
      <div style="font-family: Arial, sans-serif; line-height: 1.6; color: #2F4E3B;">
        <h2 style="color: #2F4E3B; border-bottom: 2px solid #2F4E3B; padding-bottom: 10px;">
          🎉 New Newsletter Subscription!
        </h2>
        
        <div style="background-color: #f8f9fa; padding: 20px; border-radius: 8px; margin: 20px 0;">
          <h3 style="color: #2F4E3B; margin-top: 0;">Subscriber Details:</h3>
          
          <table style="width: 100%; border-collapse: collapse;">
            <tr style="border-bottom: 1px solid #ddd;">
              <td style="padding: 10px; font-weight: bold; color: #2F4E3B; width: 150px;">📧 Email:</td>
              <td style="padding: 10px; background-color: #e3f2fd; border-radius: 4px; font-family: monospace;">
                ${subscriberEmail}
              </td>
            </tr>
            <tr style="border-bottom: 1px solid #ddd;">
              <td style="padding: 10px; font-weight: bold; color: #2F4E3B;">📅 Date & Time:</td>
              <td style="padding: 10px;">${formattedDate}</td>
            </tr>
            <tr style="border-bottom: 1px solid #ddd;">
              <td style="padding: 10px; font-weight: bold; color: #2F4E3B;">📍 Source:</td>
              <td style="padding: 10px;">${subscriptionSource}</td>
            </tr>
            <tr style="border-bottom: 1px solid #ddd;">
              <td style="padding: 10px; font-weight: bold; color: #2F4E3B;">🌐 Page:</td>
              <td style="padding: 10px;">${window.location.pathname}</td>
            </tr>
            <tr>
              <td style="padding: 10px; font-weight: bold; color: #2F4E3B;">💻 Device:</td>
              <td style="padding: 10px; font-size: 12px; color: #666;">${navigator.userAgent}</td>
            </tr>
          </table>
        </div>
        
        <div style="text-align: center; margin: 30px 0;">
          <a href="${window.location.origin}/admin/subscribers" 
             style="display: inline-block; background-color: #2F4E3B; color: white; padding: 12px 30px; text-decoration: none; border-radius: 5px; font-weight: bold;">
            View All Subscribers
          </a>
        </div>
        
        <hr style="border: none; border-top: 1px solid #eee; margin: 30px 0;">
        
        <div style="text-align: center; color: #666; font-size: 12px;">
          <p>This is an automated notification from Cittasya website.<br>
          Total subscribers are growing! 🚀</p>
          
          <p style="margin-top: 20px; padding-top: 10px; border-top: 1px solid #eee;">
            This email was sent regarding a subscription by: ${subscriberEmail}<br>
            <a href="${unsubscribeUrl}" style="color: #666; text-decoration: none;">
              Unsubscribe ${subscriberEmail} from newsletters
            </a>
          </p>
        </div>
      </div>
    `;

    const templateParams = {
      to_email: "cittasya.website@gmail.com",
      subject: "New Newsletter Subscription - Cittasya",
      html_content: htmlContent,
      // Keep the individual fields as well for template flexibility
      subscription_source: subscriptionSource,
      subscription_date: formattedDate,
      subscription_page: window.location.pathname,
      user_agent: navigator.userAgent,
      website_url: window.location.origin,
      unsubscribe_url: unsubscribeUrl,
      email: subscriberEmail,
    };

    console.log("📧 Sending admin notification with HTML content:", {
      to_email: templateParams.to_email,
      subject: templateParams.subject,
      subscriber_email: subscriberEmail,
      content_length: htmlContent.length,
    });

    const response = await emailjs.send(
      serviceId,
      templateId,
      templateParams,
      publicKey
    );
    console.log("✅ Admin notification email sent successfully:", response);
    return response;
  } catch (error) {
    console.error("❌ Error sending admin notification email:", error);
    throw error;
  }
};

// Combined function to send both emails for new subscription
export const sendSubscriptionEmails = async (
  subscriberEmail: string,
  serviceId: string,
  welcomeTemplateId: string,
  adminTemplateId: string,
  publicKey: string,
  subscriptionSource: string = "website"
) => {
  try {
    const results = {
      welcomeEmail: null as unknown,
      adminEmail: null as unknown,
      errors: [] as string[],
    };

    // Send welcome email to subscriber
    try {
      results.welcomeEmail = await sendWelcomeEmail(
        subscriberEmail,
        serviceId,
        welcomeTemplateId,
        publicKey
      );
    } catch (error) {
      results.errors.push(
        `Welcome email failed: ${
          error instanceof Error ? error.message : "Unknown error"
        }`
      );
    }

    // Send notification email to admin
    try {
      results.adminEmail = await sendAdminNotificationEmail(
        subscriberEmail,
        serviceId,
        adminTemplateId,
        publicKey,
        subscriptionSource
      );
    } catch (error) {
      results.errors.push(
        `Admin notification failed: ${
          error instanceof Error ? error.message : "Unknown error"
        }`
      );
    }

    return results;
  } catch (error) {
    console.error("❌ Error in sendSubscriptionEmails:", error);
    throw error;
  }
};
